import { useEffect, useMemo, useState } from "react";
import { ApiError } from "@shared/api";
import type { Message, Uuid } from "@shared/api";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { plainText } from "@shared/lib/markup";
import { useStore } from "@features/chat";
import { directName } from "@features/group-dm";
import { isFeed } from "@entities/channel";

/** Сколько мест можно выбрать разом — как в Discord. */
const TARGETS_MAX = 5;

/**
 * Переслать — как в Discord: куда (до пяти каналов и переписок разом), своё
 * сообщение к пересланному, если нужно, и отправить. Каналы серверов, куда ещё
 * не заходили, догружаются при открытии — иначе их не выбрать.
 */
export function ForwardDialog({ message, onClose }: { message: Message; onClose: () => void }) {
  const store = useStore();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Uuid[]>([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    for (const server of store.servers) {
      if (!store.states[server.id]) void store.loadServer(server.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const targets = useMemo(
    () => [
      ...store.directs.map((direct) => ({
        id: direct.channel_id,
        name: directName(direct),
        where: "личная переписка",
      })),
      ...store.servers.flatMap((server) =>
        (store.states[server.id]?.channels ?? [])
          .filter((channel) => isFeed(channel.channel_type))
          .map((channel) => ({ id: channel.id, name: `#${channel.name}`, where: server.name })),
      ),
    ],
    [store.directs, store.servers, store.states],
  );
  const needle = query.trim().toLowerCase();
  const shown = targets
    .filter((item) => !needle || `${item.name} ${item.where}`.toLowerCase().includes(needle))
    .slice(0, 40);

  // Что пересылается — пересылка пересылки несёт тот же снимок.
  const what = plainText(message.content || message.forwarded?.content || "") || "вложение";

  function toggle(id: Uuid) {
    setChosen((previous) =>
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : previous.length < TARGETS_MAX
          ? [...previous, id]
          : previous,
    );
  }

  /*
   * По одному месту за раз: ушедшее снимается с выбора, и после отказа
   * повтор не пошлёт туда второй раз.
   */
  async function send() {
    // Уже уходит: второй Enter разослал бы ещё не ушедшим второй раз.
    if (chosen.length === 0 || busy) return;
    setBusy(true);
    setProblem(null);
    for (const target of chosen) {
      try {
        await store.forward(target, message.id, comment);
        setChosen((previous) => previous.filter((item) => item !== target));
      } catch (error) {
        const where = targets.find((item) => item.id === target)?.name ?? "туда";
        setProblem(
          error instanceof ApiError && error.code === "slowmode"
            ? `В ${where} медленный режим — переслать туда можно чуть позже`
            : error instanceof ApiError
              ? `${where}: ${error.message}`
              : `Не вышло переслать в ${where}`,
        );
        setBusy(false);
        return;
      }
    }
    onClose();
  }

  return (
    <Dialog
      title="Переслать"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={chosen.length === 0 || busy} onClick={() => void send()}>
            {chosen.length > 1 ? `Переслать · ${chosen.length}` : "Переслать"}
          </Button>
        </>
      }
    >
      <Input
        value={query}
        placeholder="Куда: канал или переписка"
        aria-label="Куда переслать"
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="forward-list" role="listbox" aria-multiselectable aria-label="Куда переслать">
        {shown.map((item) => (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={chosen.includes(item.id)}
            className={`switcher-item ${chosen.includes(item.id) ? "on" : ""}`}
            disabled={!chosen.includes(item.id) && chosen.length >= TARGETS_MAX}
            onClick={() => toggle(item.id)}
          >
            <span className="switcher-name ellipsis">{item.name}</span>
            <span className="switcher-where ellipsis">{item.where}</span>
          </button>
        ))}
        {shown.length === 0 && <div className="switcher-none">Ничего не нашлось</div>}
      </div>
      <div className="msg-doomed">
        <span>{what}</span>
      </div>
      <Input
        value={comment}
        maxLength={2048}
        placeholder="Добавить сообщение (необязательно)"
        aria-label="Своё сообщение к пересланному"
        onChange={(event) => setComment(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && chosen.length > 0) void send();
        }}
      />
      {problem && <p className="small danger-text" style={{ margin: 0 }}>{problem}</p>}
    </Dialog>
  );
}
